/**
 * FHIR R5 Medications Page
 * Complete medication management with FHIR R5 compliance
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Pill,
  AlertTriangle,
  CheckCircle,
  Clock,
  Filter,
  Search,
} from "lucide-react";
import { useI18nStore } from "@/stores/i18nStore";
import { toast } from "react-hot-toast";
import {
  useFHIRMedications,
  useActiveMedications,
  useFHIRMedicationStats,
  useFHIRMedicationActions,
  useFHIRMedicationTerminologies,
  useExpiringMedications,
} from "@/hooks/useFHIRMedications";
import { medicationDisplayHelpers } from "@/services/api/fhirMedicationService";
import type { MedicationDisplay } from "@/types/fhir";
import { EnhancedMedicationCard } from "@/components/medications/EnhancedMedicationCard";
import { log } from "@/lib/log";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";

export const FHIRMedicationsPage: React.FC = () => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const navigate = useNavigate();

  // FHIR Hooks
  const { medications, loading, error, clearError } = useFHIRMedications();
  const { activeMedications, loading: activeLoading } =
    useActiveMedications(false); // Don't auto-fetch - we already have all medications
  const { statistics, loading: statsLoading } = useFHIRMedicationStats(); // Now calculates locally from medications
  const { categories } = useFHIRMedicationTerminologies();
  const { expiringMedications, expiredMedications } = useExpiringMedications(); // Now safe - filters locally without overwriting store
  const { deleteMedication: deleteFHIRMedication } = useFHIRMedicationActions();

  // Local State
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Show errors as toasts
  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  // Filter medications (with null safety)
  const filteredMedications = (medications || []).filter((med) => {
    const matchesStatus = filterStatus === "all" || med.status === filterStatus;
    const matchesCategory =
      filterCategory === "all" ||
      !med.category ||
      med.category?.toLowerCase().includes(filterCategory.toLowerCase());
    const matchesSearch =
      searchTerm === "" ||
      med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      med.category?.toLowerCase().includes(searchTerm.toLowerCase());

    // Debug removed - filter working correctly

    return matchesStatus && matchesCategory && matchesSearch;
  });

  // Handle Delete
  const handleDelete = async (id: string) => {
    log.debug("🗑️ Delete clicked for medication ID:", id);
    log.debug(
      "📊 Current medications count BEFORE delete:",
      medications.length,
    );

    if (!confirm(t.medxPageConfirmDelete)) {
      log.debug("❌ Delete cancelled by user");
      return;
    }

    log.debug("⏳ Calling deleteFHIRMedication...");
    const success = await deleteFHIRMedication(id);
    log.debug("✅ Delete result:", success);

    if (success) {
      toast.success(t.medxPageDeleted);
      log.debug(
        "📊 Current medications count AFTER delete:",
        medications.length,
      );
      // The store's deleteMedication already calls:
      // - fetchAllMedications() → updates medications array → useFHIRMedicationStats recalculates
      // - fetchActiveMedications() → updates activeMedications
      // - fetchStatistics() → updates statistics
      // No additional refetch needed here, the store handles everything
    } else {
      console.error("❌ Delete failed - no success response");
    }
  };

  // Handle Edit - Navigate to edit page instead of modal
  const handleEdit = (medication: MedicationDisplay) => {
    navigate(`/medications/${medication.id}/edit`);
  };

  // Get status badge color
  const getStatusBadgeClass = (status: string): string => {
    const color = medicationDisplayHelpers.getStatusColor(status);
    const colorMap: Record<string, string> = {
      green: "bg-green-100 text-green-800",
      blue: "bg-primary-100 text-primary-800",
      red: "bg-red-100 text-red-800",
      yellow: "bg-yellow-100 text-yellow-800",
      purple: "bg-purple-100 text-purple-800",
      gray: "bg-gray-100 text-gray-800",
    };
    return colorMap[color] || "bg-gray-100 text-gray-800";
  };

  if (loading && medications.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t.medxPageLoading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Pill className="h-8 w-8 text-primary-600" />
            {t.medicationsPage}
          </h1>
          <p className="text-gray-600 mt-2">{t.medxPageSubtitle}</p>
        </div>
        <button
          onClick={() => navigate("/traveler/medications/add")}
          className="shrink-0 self-start sm:self-auto whitespace-nowrap bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>{t.addNewMedication}</span>
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Medications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{t.medxPageTotal}</p>
              <p className="text-3xl font-bold text-gray-900">
                {statsLoading ? "..." : fmt.number(statistics?.total || 0)}
              </p>
            </div>
            <div className="bg-primary-100 rounded-full p-3">
              <Pill className="h-6 w-6 text-primary-600" />
            </div>
          </div>
        </div>

        {/* Active Medications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{t.medxPageActive}</p>
              <p className="text-3xl font-bold text-green-600">
                {activeLoading ? "..." : fmt.number(activeMedications.length)}
              </p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Expiring Soon */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">
                {t.medxPageExpiringSoon}
              </p>
              <p className="text-3xl font-bold text-yellow-600">
                {fmt.number(expiringMedications.length)}
              </p>
            </div>
            <div className="bg-yellow-100 rounded-full p-3">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Expired */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{t.medxPageExpired}</p>
              <p className="text-3xl font-bold text-red-600">
                {fmt.number(expiredMedications.length)}
              </p>
            </div>
            <div className="bg-red-100 rounded-full p-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t.medxPageSearch}
              aria-label={t.medxPageSearch}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={filterStatus}
              aria-label={t.medxFormStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
            >
              <option value="all">{t.medxPageAllStatuses}</option>
              <option value="active">{t.medxStatusActive}</option>
              <option value="completed">{t.medxStatusCompleted}</option>
              <option value="stopped">{t.medxStatusStopped}</option>
              <option value="on-hold">{t.medxStatusOnHold}</option>
            </select>
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={filterCategory}
              aria-label={t.medxCardCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
            >
              <option value="all">{t.medxPageAllCategories}</option>
              {categories?.map((cat) => (
                <option key={cat.code} value={cat.code}>
                  {cat.display}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Medications List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {t.medxPageListTitle.replace(
              "{count}",
              fmt.number(filteredMedications.length),
            )}
          </h2>
        </div>

        {filteredMedications.length === 0 ? (
          <div className="p-12 text-center">
            <Pill className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">{t.medxPageEmpty}</p>
            <p className="text-gray-400 text-sm mb-6">
              {searchTerm || filterStatus !== "all" || filterCategory !== "all"
                ? t.medxPageEmptyFiltered
                : t.medxPageEmptyStart}
            </p>
            {!searchTerm &&
              filterStatus === "all" &&
              filterCategory === "all" && (
                <button
                  onClick={() => navigate("/traveler/medications/add")}
                  className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center space-x-2"
                >
                  <Plus className="h-5 w-5" />
                  <span>{t.medxPageAddFirst}</span>
                </button>
              )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredMedications.map((medication) => (
              <EnhancedMedicationCard
                key={medication.id}
                medication={medication}
                onEdit={handleEdit}
                onDelete={handleDelete}
                getStatusBadgeClass={getStatusBadgeClass}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
